/*
 * 6tm-multi-variant-calculator.js
 * --------------------------------------------------------------
 * Vanilla JS (zero Alpine, zero SVG).
 *
 * Modele :
 *   - 1 dropdown "styles" : checkboxes [data-style], mutex Font Family <-> individuels
 *   - 4 dropdowns "licences" : pour chacune (desktop/web/app/advertising), liste
 *     de paliers [data-tier] avec data-license-key + data-multiplier.
 *     Allow-one : checkbox visuelle mais comportement radio (un seul palier coche
 *     par licence a la fois).
 *   - Section "Selected" : liste live des combinaisons (Style coche X Licence
 *     active) avec prix unitaire (= variant.price * multiplicateur).
 *   - Total + Add to cart : POST /cart/add.js items[] avec 1 item par couple
 *     (Style, Licence active), quantity = multiplicateur du palier.
 *
 * Doc :
 *   - product.variants | json : https://shopify.dev/docs/api/liquid/objects/variant
 *   - /cart/add.js : https://shopify.dev/docs/api/ajax/reference/cart
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  ready(function () {
    document.querySelectorAll('[data-multi-picker]').forEach(initPicker);
  });

  /**
   * Normalise la propriete `price` d'une variant en cents (int).
   * Shopify expose price diversement (string major-unit "19.00" ou int cents 1900).
   */
  function toCents(rawPrice) {
    if (rawPrice === null || rawPrice === undefined) return 0;
    if (typeof rawPrice === 'number') {
      if (!Number.isInteger(rawPrice)) return Math.round(rawPrice * 100);
      return rawPrice;
    }
    const str = String(rawPrice).trim();
    if (str.indexOf('.') === -1) {
      const n = parseInt(str, 10);
      return isNaN(n) ? 0 : n;
    }
    const f = parseFloat(str);
    if (isNaN(f)) return 0;
    return Math.round(f * 100);
  }

  function initPicker(root) {
    const variantsRaw = root.querySelector('[data-variants]');
    if (!variantsRaw) return;
    let variants;
    try {
      variants = JSON.parse(variantsRaw.textContent);
    } catch (e) {
      console.error('[6tm-multi-variant] Cannot parse variants JSON', e);
      return;
    }
    if (!Array.isArray(variants) || variants.length === 0) return;

    const primaryPosition = parseInt(root.dataset.primaryPosition, 10) || 1;
    const secondaryPosition = parseInt(root.dataset.secondaryPosition, 10) || 2;
    const fontFamilyValue = (root.dataset.fontFamilyValue || 'Font Family').toLowerCase();

    const styleInputs = root.querySelectorAll('input[data-style]');
    const tierInputs = root.querySelectorAll('input[data-tier]');
    const fontFamilyInput = root.querySelector('input[data-is-font-family="true"]');
    const individualStyleInputs = root.querySelectorAll('input[data-style]:not([data-is-font-family="true"])');
    const totalEl = root.querySelector('[data-total]');
    const heroPriceEl = root.querySelector('[data-hero-price]');
    const btn = root.querySelector('[data-add-button]');
    const selectedEl = root.querySelector('[data-selected]');
    const selectedListEl = root.querySelector('[data-selected-list]');
    const licenseDropdowns = root.querySelectorAll('[data-tdrop-kind="license"]');
    const allDropdowns = root.querySelectorAll('[data-tdrop]');

    const fallbackCents = parseInt(root.dataset.fallbackCents, 10);
    const heroFallbackCents = isNaN(fallbackCents) ? 0 : fallbackCents;
    const fallbackCompareCents = parseInt(root.dataset.fallbackCompareCents, 10);
    const heroFallbackCompareCents = isNaN(fallbackCompareCents) ? 0 : fallbackCompareCents;

    if (!btn || !totalEl) return;

    function getVariantOption(v, position) {
      if (Array.isArray(v.options) && v.options.length >= position) {
        return v.options[position - 1];
      }
      const key = 'option' + position;
      if (v[key] !== undefined && v[key] !== null) return v[key];
      return '';
    }

    function findVariant(primaryValue, secondaryValue) {
      const pLower = String(primaryValue).toLowerCase().trim();
      const sLower = String(secondaryValue).toLowerCase().trim();
      for (let i = 0; i < variants.length; i++) {
        const v = variants[i];
        if (!v) continue;
        const vp = String(getVariantOption(v, primaryPosition) || '').toLowerCase().trim();
        const vs = String(getVariantOption(v, secondaryPosition) || '').toLowerCase().trim();
        if (vp === pLower && vs === sLower) return v;
      }
      return null;
    }

    function formatMoney(cents) {
      if (typeof window.formatMoney === 'function') {
        try {
          return window.formatMoney(cents);
        } catch (e) {
          /* noop */
        }
      }
      return (cents / 100).toFixed(2).replace('.', ',') + ' EUR';
    }

    function selectedStyleValues() {
      const out = [];
      for (let i = 0; i < styleInputs.length; i++) {
        if (styleInputs[i].checked) {
          out.push({
            value: styleInputs[i].value,
            isFontFamily: styleInputs[i].dataset.isFontFamily === 'true'
          });
        }
      }
      return out;
    }

    function activeLicenseTiers() {
      // Une licence "active" = un et un seul palier coche pour cette licence.
      const out = [];
      licenseDropdowns.forEach(function (drop) {
        const key = drop.dataset.licenseKey || '';
        const usageValue = drop.dataset.usageValue || '';
        const checked = drop.querySelector('input[data-tier]:checked');
        if (checked) {
          out.push({
            licenseKey: key,
            usageValue: usageValue,
            tierLabel: checked.value,
            multiplier: parseInt(checked.dataset.multiplier, 10) || 1,
            triggerLabel: drop.querySelector('[data-tdrop-trigger] .tpm-tdrop__trigger-label')
              ? drop.querySelector('[data-tdrop-trigger] .tpm-tdrop__trigger-label').textContent.trim()
              : key
          });
        }
      });
      return out;
    }

    function computeCombinations() {
      const styles = selectedStyleValues();
      const tiers = activeLicenseTiers();
      const combos = [];
      let totalCents = 0;
      let totalCompareCents = 0;
      const items = [];

      for (let i = 0; i < styles.length; i++) {
        for (let j = 0; j < tiers.length; j++) {
          const style = styles[i];
          const tier = tiers[j];
          const variant = findVariant(style.value, tier.usageValue);
          if (variant && variant.available) {
            const unitCents = toCents(variant.price);
            const itemCents = unitCents * tier.multiplier;
            /* Ancien prix = compare_at_price s'il est superieur au prix, sinon le prix
               lui-meme (une variante non remisee ne gonfle pas le total barre). */
            const compareUnitCents = Math.max(toCents(variant.compare_at_price), unitCents);
            const itemCompareCents = compareUnitCents * tier.multiplier;
            combos.push({
              styleLabel: style.value,
              licenseLabel: tier.triggerLabel,
              tierLabel: tier.tierLabel,
              multiplier: tier.multiplier,
              variantId: variant.id,
              itemCents: itemCents,
              itemCompareCents: itemCompareCents
            });
            /* Line item properties prefixees "_" : cachees au checkout,
               lues cote Liquid dans cart-items pour l'affichage groupe. */
            items.push({
              id: variant.id,
              quantity: tier.multiplier,
              properties: {
                _6tm_style: style.value,
                _6tm_licence: tier.triggerLabel,
                _6tm_tier: tier.tierLabel,
                _6tm_unit_cents: String(unitCents)
              }
            });
            totalCents += itemCents;
            totalCompareCents += itemCompareCents;
          }
        }
      }

      return { combos: combos, items: items, totalCents: totalCents, totalCompareCents: totalCompareCents };
    }

    /**
     * Ecrit un prix dans `el` avec le meme markup que le bloc prix natif :
     * prix courant en text-scheme-accent + ancien prix dans <s> quand remise.
     * Libelles sr-only optionnels (hero uniquement).
     */
    function renderPrice(el, cents, compareCents, labels) {
      el.innerHTML = '';
      const onSale = compareCents > cents;
      if (labels && labels.regular) {
        const sr = document.createElement('span');
        sr.className = 'sr-only';
        sr.textContent = labels.regular;
        el.appendChild(sr);
      }
      const current = document.createElement('span');
      if (onSale) current.className = 'text-scheme-accent';
      current.textContent = formatMoney(cents);
      el.appendChild(current);
      if (!onSale) return;
      el.appendChild(document.createTextNode(' '));
      const wrap = document.createElement('span');
      if (labels && labels.sale) {
        const sr = document.createElement('span');
        sr.className = 'sr-only';
        sr.textContent = labels.sale;
        wrap.appendChild(sr);
      }
      const s = document.createElement('s');
      s.textContent = formatMoney(compareCents);
      wrap.appendChild(s);
      el.appendChild(wrap);
    }

    function renderSelected(combos) {
      if (!selectedListEl) return;
      selectedListEl.innerHTML = '';
      if (combos.length === 0) {
        if (selectedEl) selectedEl.setAttribute('hidden', '');
        return;
      }
      if (selectedEl) selectedEl.removeAttribute('hidden');
      for (let i = 0; i < combos.length; i++) {
        const c = combos[i];
        const li = document.createElement('li');
        li.className = 'tpm-selected__row';

        const meta = document.createElement('div');
        meta.className = 'tpm-selected__meta';

        const styleSpan = document.createElement('span');
        styleSpan.className = 'tpm-selected__style';
        styleSpan.textContent = c.styleLabel;
        meta.appendChild(styleSpan);

        const licenseSpan = document.createElement('span');
        licenseSpan.className = 'tpm-selected__license';
        licenseSpan.textContent = c.licenseLabel + ' - ' + c.tierLabel;
        meta.appendChild(licenseSpan);

        const priceSpan = document.createElement('span');
        priceSpan.className = 'tpm-selected__price';
        renderPrice(priceSpan, c.itemCents, c.itemCompareCents);

        li.appendChild(meta);
        li.appendChild(priceSpan);
        selectedListEl.appendChild(li);
      }
    }

    function updateTriggerSummaries() {
      // Styles dropdown
      const styleDrop = root.querySelector('[data-tdrop-kind="styles"]');
      if (styleDrop) {
        const styleSummaryEl = styleDrop.querySelector('[data-tdrop-summary]');
        const styles = selectedStyleValues();
        if (styleSummaryEl) {
          if (styles.length === 0) {
            styleSummaryEl.textContent = '';
          } else if (styles.length === 1) {
            styleSummaryEl.textContent = styles[0].value;
          } else {
            styleSummaryEl.textContent = styles.length + ' styles';
          }
        }
      }
      // Licences
      licenseDropdowns.forEach(function (drop) {
        const summaryEl = drop.querySelector('[data-tdrop-summary]');
        const checked = drop.querySelector('input[data-tier]:checked');
        if (summaryEl) {
          summaryEl.textContent = checked ? checked.value : '';
        }
      });
    }

    function updateUI() {
      const result = computeCombinations();
      renderSelected(result.combos);
      totalEl.textContent = formatMoney(result.totalCents);
      if (heroPriceEl) {
        const hasTotal = result.totalCents > 0;
        renderPrice(
          heroPriceEl,
          hasTotal ? result.totalCents : heroFallbackCents,
          hasTotal ? result.totalCompareCents : heroFallbackCompareCents,
          { regular: heroPriceEl.dataset.regularLabel, sale: heroPriceEl.dataset.saleLabel }
        );
      }
      btn.disabled = result.items.length === 0;
      updateTriggerSummaries();
      return result;
    }

    // --- Style mutex ---
    function handleStyleToggle(e) {
      const target = e.target;
      if (!target || target.type !== 'checkbox') return;
      const isFontFamily = target.dataset.isFontFamily === 'true';

      if (isFontFamily && target.checked) {
        for (let i = 0; i < individualStyleInputs.length; i++) {
          individualStyleInputs[i].checked = false;
        }
      } else if (!isFontFamily && target.checked) {
        if (fontFamilyInput) fontFamilyInput.checked = false;
      }
      updateUI();
    }

    for (let i = 0; i < styleInputs.length; i++) {
      styleInputs[i].addEventListener('change', handleStyleToggle);
    }

    // --- Tier allow-one (radio-like) ---
    function handleTierToggle(e) {
      const target = e.target;
      if (!target || target.type !== 'checkbox') return;
      const licenseKey = target.dataset.licenseKey;
      if (!licenseKey) return;
      if (target.checked) {
        // Decoche les autres paliers de la meme licence
        const siblings = root.querySelectorAll('input[data-tier][data-license-key="' + licenseKey + '"]');
        for (let i = 0; i < siblings.length; i++) {
          if (siblings[i] !== target) siblings[i].checked = false;
        }
      }
      updateUI();
    }

    for (let i = 0; i < tierInputs.length; i++) {
      tierInputs[i].addEventListener('change', handleTierToggle);
    }

    // --- Dropdown open/close ---
    function closeAllExcept(except) {
      allDropdowns.forEach(function (drop) {
        if (drop === except) return;
        drop.classList.remove('is-open');
        const trig = drop.querySelector('[data-tdrop-trigger]');
        if (trig) trig.setAttribute('aria-expanded', 'false');
      });
    }

    allDropdowns.forEach(function (drop) {
      const trig = drop.querySelector('[data-tdrop-trigger]');
      if (!trig) return;
      trig.addEventListener('click', function () {
        const open = drop.classList.toggle('is-open');
        trig.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) closeAllExcept(drop);
      });
    });

    // Click outside closes all
    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) {
        closeAllExcept(null);
      }
    });

    // --- Add to cart ---
    btn.addEventListener('click', function () {
      const result = computeCombinations();
      if (!result.items.length) {
        return;
      }
      btn.disabled = true;
      const original = btn.textContent;
      btn.textContent = 'Adding...';

      /* Demande le rendu des sections cart en meme temps que l'add.
         Ce sont ces sections que Baseline morph dans le drawer et le count.
         Sans ce parametre, /cart/add.js retourne le cart mais aucune section rendue,
         et le drawer reste figee sur son etat initial (souvent "panier vide"). */
      const sectionsList = 'cart-items,cart-footer,cart-item-count,cart-live-region';
      const sectionsUrl = window.location.pathname;
      const addUrl = '/cart/add.js?sections=' + encodeURIComponent(sectionsList) +
        '&sections_url=' + encodeURIComponent(sectionsUrl);

      fetch(addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: result.items })
      })
        .then(function (resp) {
          if (!resp.ok) {
            return resp.text().then(function (t) {
              throw new Error('Cart add failed: ' + resp.status + ' ' + t);
            });
          }
          return resp.json();
        })
        .then(function (data) {
          btn.textContent = 'Added';

          /* Event canonique Baseline pour refresh du drawer + count + footer.
             cart-items.js / cart-count.js / cart-footer.js / modal-cart.js
             ecoutent tous baseline:cart:afteradditem sur document.body. */
          try {
            document.body.dispatchEvent(new CustomEvent('baseline:cart:afteradditem', {
              bubbles: true,
              detail: {
                response: data,
                sourceId: '6tm-multi-variant-picker'
              }
            }));
          } catch (e) { /* noop */ }

          /* Compatibilite legacy pour HulkApps / autres listeners custom. */
          const legacyEvents = [
            'cart:refresh',
            'cart:update',
            'theme:cart:update',
            'theme:cart:refresh',
            'hulkapps:cart-refresh',
            'hulkapps:refresh'
          ];
          for (let i = 0; i < legacyEvents.length; i++) {
            const evtName = legacyEvents[i];
            try {
              document.dispatchEvent(new CustomEvent(evtName, { detail: data }));
              window.dispatchEvent(new CustomEvent(evtName, { detail: data }));
            } catch (e) { /* noop */ }
          }
          setTimeout(function () {
            btn.textContent = original;
            updateUI();
          }, 1500);
        })
        .catch(function (err) {
          console.error('[6tm-multi-variant] cart add error', err);
          btn.textContent = 'Error';
          setTimeout(function () {
            btn.textContent = original;
            btn.disabled = false;
          }, 2000);
        });
    });

    updateUI();
  }
})();
